import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Progress, Spin, Tooltip } from 'antd';
import { FileDownloadStatusEnum } from '../../enums/FileDownloadStatusEnum';
import { useFileDownload } from '../../hooks/useFileDownload';
import type ProcessStatus from '../../interfaces/ProcessStatus';
import { Icon } from '../Core/Icon';
import TruncatedTooltip from './TruncatedTooltip';

const DownloadProgressDrawer = () => {
  const { t } = useTranslation();
  const [isMinimized, setIsMinimized] = useState(false);
  const prevProcessStatusesRef = useRef<ProcessStatus[]>([]);
  const { processStatuses, handleCancelAllProcesses } = useFileDownload();

  /**
   * Title representing the current state of a process based on its status.
   *
   * This memoized value determines the appropriate label to display depending on the
   * status of processes within `processStatuses`.
   * If any process is marked as `InProgress`, it returns the label for 'downloadPreparation';
   * otherwise, it returns the label for 'downloadReady'.
   *
   * Dependencies:
   * - processStatuses: Array of process objects to check their statuses.
   */
  const title: string = useMemo(() => {
    if (processStatuses.some((process) => process.status === FileDownloadStatusEnum.InProgress)) {
      return t('downloadPreparation');
    } else {
      return t('downloadReady');
    }
  }, [processStatuses]);

  /**
   * Indicates whether there are any active processes with a status of 'InProgress'.
   * This variable memoizes the result to improve performance by avoiding unnecessary recalculations.
   *
   * @type {boolean}
   */
  const areActiveProcesses: boolean = useMemo(() => {
    return processStatuses.some((process) => process.status === FileDownloadStatusEnum.InProgress);
  }, [processStatuses]);

  useEffect(() => {
    if (isMinimized) {
      setIsMinimized(false);
    }
  }, [processStatuses.length]);

  useEffect(() => {
    const prevProcessStatuses = prevProcessStatusesRef.current;

    // Get the IDs of previous processes with status InProgress
    const prevInProgressIds = prevProcessStatuses
      .filter((p) => p.status === FileDownloadStatusEnum.InProgress)
      .map((p) => p.processId);

    // Get the IDs of current processes with status InProgress
    const currentInProgress = processStatuses.filter(
      (p) => p.status === FileDownloadStatusEnum.InProgress,
    );

    // Check if there are any new processes with status InProgress
    const hasNewInProgress = currentInProgress.some(
      (p) => !prevInProgressIds.includes(p.processId),
    );

    if (hasNewInProgress) {
      setIsMinimized(false);
    }

    // Update the reference to the current process statuses
    prevProcessStatusesRef.current = processStatuses;
  }, [processStatuses]);

  // If there are no processes, don't render the drawer
  if (processStatuses.length === 0) {
    return null;
  }

  return (
    <div
      id="download-progress-drawer"
      className={clsx(
        'z-50 w-full rounded-md border border-gray-medium bg-white transition-all duration-200 md:w-[400px] lg:w-[500px]',
        isMinimized ? 'h-12' : 'h-80',
      )}
    >
      <div
        onClick={() => setIsMinimized(!isMinimized)}
        className={clsx(
          'flex items-center justify-between rounded-t-md border-b bg-gray-light px-3 py-2',
          isMinimized ? 'border-transparent' : 'border-gray-medium',
        )}
      >
        <div className="text-base font-medium text-blue-dark">{title}</div>
        <div className="flex items-center">
          <Button
            type="text"
            size="small"
            className="flex items-center pl-0"
            icon={
              <Icon
                name="ChevronDownIcon"
                className={clsx(
                  'mt-1 h-5 w-5 transform transition-transform duration-200',
                  isMinimized ? 'rotate-180' : '',
                )}
              />
            }
          />
          <Button
            type="text"
            size="small"
            onClick={() => handleCancelAllProcesses()}
            disabled={areActiveProcesses}
            className="flex items-center pl-0"
            icon={<Icon name="XIcon" className="mt-1 h-4 w-4" />}
          />
        </div>
      </div>
      {!isMinimized && (
        <div className="flex h-full max-h-fit flex-col divide-y divide-gray overflow-y-auto px-3 pb-14">
          {processStatuses.map((process) => (
            <div
              key={process.processId}
              className="relative flex min-h-[35px] items-center justify-between transition-all duration-200"
            >
              <div className="flex items-center">
                <div className="max-w-60 truncate">
                  <div className="text-sm font-medium text-blue-dark">
                    <TruncatedTooltip>
                      {((): string => {
                        switch (process.status) {
                          case FileDownloadStatusEnum.Pending:
                            return t('common.preparingFileForDownload');
                          case FileDownloadStatusEnum.InProgress:
                            return t('compressionOfFilesInProgress');
                          case FileDownloadStatusEnum.Success:
                            return process.name || t('compressionCompleted');
                          case FileDownloadStatusEnum.Failed:
                            return t('compressionFailed');
                          default:
                            return 'N/D';
                        }
                      })()}
                    </TruncatedTooltip>
                  </div>
                </div>
              </div>
              <div className="flex items-center">
                {(() => {
                  switch (process.status) {
                    case FileDownloadStatusEnum.Pending:
                      return <Spin size="small" />;
                    case FileDownloadStatusEnum.InProgress:
                      return (
                        <div>
                          <span className="text-sm">{process.progressPercentage}%</span>
                        </div>
                      );
                    case FileDownloadStatusEnum.Success:
                      return (
                        <div className="flex items-center">
                          <Tooltip placement="left" title={t('common.download')}>
                            <Button
                              type="text"
                              size="small"
                              className="px-2 py-0.5"
                              onClick={() => {
                                // Initiate download
                                const downloadUrl = process.additionalData;
                                if (downloadUrl) {
                                  const link = document.createElement('a');
                                  link.href = downloadUrl;
                                  link.setAttribute('download', '');
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                }
                              }}
                            >
                              <Icon name="CloudDownloadIcon" className="mt-0.5 h-5 w-5" />
                            </Button>
                          </Tooltip>
                          <Icon name="CircleCheckIcon" className="h-5 w-5 text-green-dark" />
                        </div>
                      );
                    case FileDownloadStatusEnum.Failed:
                      return (
                        <div className="flex items-center gap-0.5">
                          {/* Error icon */}
                          <Icon name="CircleAlertIcon" className="h-5 w-5 text-red-dark" />
                          <span className="text-sm text-red-dark">{t('common.failed')}</span>
                        </div>
                      );
                    default:
                      return 'N/D';
                  }
                })()}
              </div>
              {[
                FileDownloadStatusEnum.InProgress,
                FileDownloadStatusEnum.Pending,
                FileDownloadStatusEnum.Failed,
              ].includes(process.status) && (
                <Progress
                  status={process.status === FileDownloadStatusEnum.Failed ? 'exception' : 'active'}
                  className="compact-progress absolute -bottom-2 left-0 right-0 z-10"
                  size="small"
                  percent={process.progressPercentage ?? 0}
                  showInfo={false}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DownloadProgressDrawer;
